import React, { useState, useRef, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { login } from "actions/auth";
import { Spinner } from "components/ui";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { Button } from "components/ui_new";
import { useHistory } from "react-router-dom";
import selectors from "selectors";
import { translateUsingBrowser } from "utils/misc";
import secureLocalStorage from "react-secure-storage";
import MultifactorModalContainer from "./multifactor-modal-container";
import useDocumentTitle from "hooks/use-document-title";
import "./login-pages.scss";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export const Login = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const loginError = useSelector(selectors.getLoginError);
  const loginMessage = useSelector(selectors.getLoginMessage);
  const userMFADetails = useSelector(selectors.getUserMFADetails);

  useDocumentTitle(translateUsingBrowser("login"), "WellSky");

  const getFieldError = (field, errors, touched) => {
    return touched[field] ? errors[field] : "";
  };
  const rememberMeValue = secureLocalStorage.getItem("rememberMe");
  const [showPassword, setShowPassword] = useState(false);
  const [visibleMessage, setVisibleMessage] = useState(null);
  const formikRef = useRef(null);

  const handleClickShowPassword = () => setShowPassword(show => !show);

  // Delayed injection for screen reader announcement
  useEffect(() => {
    if (loginMessage) {
      // Small delay to ensure live region is in DOM before content appears
      const timer = setTimeout(() => {
        setVisibleMessage(loginMessage);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setVisibleMessage(null);
    }
  }, [loginMessage]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formikRef.current) return;

    const errors = await formikRef.current.validateForm();
    formikRef.current.setTouched(
      Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    if (Object.keys(errors).length > 0) {
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.focus();
      }
      return;
    }

    formikRef.current.handleSubmit();
  };

  return (
    <div className="login-screen" role="main">
      <MultifactorModalContainer
        open={userMFADetails.MFAAuthRequired || false}
        userMFADetails={userMFADetails}
      />
      <div className="login-background" aria-hidden="true">
        <div className="white-background"></div>
        <div className="color-background"></div>
      </div>

      <header className="login-screen-header">
        <div className="login-logo" role="img" aria-label="My Sleep Coach Powered by WellSky" />
      </header>

      <div className="login-container">
        <div className="login-form-card" role="form">
          <h1 className="login-card-header" id="login-title">
            {translateUsingBrowser("welcome")}
          </h1>

          <Formik
            innerRef={formikRef}
            initialValues={{
              email: rememberMeValue ?? "",
              password: "",
              rememberMe: rememberMeValue != null
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .required(translateUsingBrowser("emailIsRequired"))
                .email(translateUsingBrowser("enterValidEmail")),
              password: Yup.string().required(
                translateUsingBrowser("passwordIsRequired")
              ),
              rememberMe: Yup.boolean()
            })}
            onSubmit={async values => {
              await dispatch(login(values));
            }}
          >
            {({
              isSubmitting,
              handleChange,
              errors,
              touched,
              handleBlur,
              values
            }) => (
              <form onSubmit={handleFormSubmit} aria-labelledby="login-title">
                <div className="form-field">
                  <TextField
                    id="email"
                    label={translateUsingBrowser("email")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    name="email"
                    error={!!getFieldError("email", errors, touched)}
                    helperText={getFieldError("email", errors, touched)}
                    type="email"
                    autoComplete="email"
                    placeholder={translateUsingBrowser("enterEmail")}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                </div>

                <div className="form-field">
                  <TextField
                    id="password"
                    label={translateUsingBrowser("password")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    name="password"
                    error={!!getFieldError("password", errors, touched)}
                    helperText={getFieldError("password", errors, touched)}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder={translateUsingBrowser("enterPassword")}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              onClick={handleClickShowPassword}
                              edge="end"
                              type="button"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </div>

                <div className="remember-me-container">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.rememberMe}
                        onChange={handleChange}
                        name="rememberMe"
                        id="rememberMe"
                        className="checkbox"
                      />
                    }
                    label={translateUsingBrowser("rememberMe")}
                    className="label"
                  />
                </div>

                <div className="login-info" role="status" aria-live="polite">
                  {visibleMessage && (
                    <div className="login-message">
                      {visibleMessage}
                    </div>
                  )}
                  {loginError && (
                    <div className="login-error" role="alert">
                      {loginError}
                    </div>
                  )}
                </div>

                {isSubmitting ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 120
                    }}
                    role="status"
                    aria-label="Logging in"
                  >
                    <Spinner />
                  </div>
                ) : (
                  <div className="login-form-actions">
                    <Button
                      variant="contained"
                      color="primary"
                      id="login-button"
                      type="submit"
                      fullWidth
                      disableRipple
                      disableElevation
                    >
                      {translateUsingBrowser("login")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      id="first-time-login-button"
                      type="button"
                      fullWidth
                      disableRipple
                      onClick={() => {
                        history.push("/register");
                      }}
                    >
                      {translateUsingBrowser("firstTimeLogin")}
                    </Button>

                    <div className="login-links">
                      <Button
                        variant="text"
                        disableRipple
                        onClick={() => {
                          history.push("/forgot-password");
                        }}
                      >
                        {translateUsingBrowser("forgotPassword")}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Login;
